import type { DeliveryAttentionItem } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BlockerCardProps {
  item: DeliveryAttentionItem;
  onExecute: () => void;
  onView: () => void;
}

function getConvergenceColor(score: number | null): string {
  if (score === null) return 'text-text-tertiary';
  if (score >= 90) return 'text-success';
  if (score >= 70) return 'text-warning';
  return 'text-error';
}

function BlockerCard({ item, onExecute, onView }: BlockerCardProps): React.ReactElement {
  const { outcome, hardBlocked, blockedBy, message } = item;

  return (
    <div className="rounded-lg border border-warning/30 bg-warning/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs text-text-secondary">{outcome.id}</p>
          <p className="mt-1 text-sm font-medium text-text-primary">{outcome.title}</p>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant="secondary">{outcome.status}</Badge>
            <span
              className={cn('text-xs font-medium', getConvergenceColor(outcome.convergenceScore))}
            >
              {outcome.convergenceScore !== null ? `${outcome.convergenceScore}%` : '--'}
            </span>
          </div>
          <div className="mt-2 text-xs font-medium text-warning">
            {hardBlocked ? `Blocked by ${blockedBy.join(', ')}` : message}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            size="sm"
            disabled={hardBlocked}
            title={hardBlocked ? `Blocked by: ${blockedBy.join(', ')}` : message}
            onClick={onExecute}
          >
            {hardBlocked ? 'Blocked' : 'Execute'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onView}>
            View
          </Button>
        </div>
      </div>
    </div>
  );
}

export { BlockerCard };
export type { BlockerCardProps };
